import { galateaSchema } from '@inkitt/titan/schemas/galatea';
import { createSelectSchema } from 'drizzle-zod';
import { z } from 'zod';
import { c } from '../../contract';
import { GetCommentsResponse } from '../../types';

export const usersStoriesComments = createSelectSchema(galateaSchema.userStoriesComments);

export const userStoriesCommentsRouter = c.router(
  {
    getCommentsWithLikesInfo: {
      summary: 'Get comment for story episode',
      method: 'GET',
      path: '',
      query: z.object({
        story: z.string(),
        episode: z.coerce.number(),
        page: z.number().optional(),
        per_page: z.number().optional(),
        sort_by: z.string().optional(),
        parent_comment_id: z.number().optional(),
        show_subscribed_only: z.boolean().optional(),
        ranking: z.enum(['sentiment']).optional(),
      }),
      responses: {
        200: z.array(GetCommentsResponse),
      },
    },
    getCommentsCountForEpisode: {
      summary: 'Get comments count for episode',
      method: 'GET',
      path: '/episode-comment-count',
      query: z.object({
        story: z.string(),
        episode: z.number().nullish(),
        exclude_blocked_users: z.boolean().optional(),
      }),
      responses: {
        200: z.object({
          count: z.number(),
        }),
      },
    },
    increaseStoryEpisodeCommentCount: {
      summary: 'Increase comments count for episode',
      method: 'PUT',
      path: '/increase-episode-comment-count',
      body: z.object({
        story: z.string(),
        episode: z.number(),
      }),
      responses: {
        200: z.object({
          count: z.number(),
        }),
      },
    },
    decreaseStoryEpisodeCommentCount: {
      summary: 'Decrease comments count for episode',
      method: 'PUT',
      path: '/decrease-episode-comment-count',
      body: z.object({
        story: z.string(),
        episode: z.number(),
      }),
      responses: {
        200: z.object({
          count: z.number(),
        }),
      },
    },
    postComment: {
      summary: 'Create comment for story episode',
      method: 'POST',
      path: '',
      body: z.object({
        story: z.string(),
        body: z.string(),
        episode: z.number(),
        username: z.string(),
        user_id: z.string(),
        firebase_id: z.string(),
        body_length: z.number().optional(),
        ts: z.number(),
        path: z.string(),
        parent_comment_id: z.number().optional(),
        user_avatar: z.string().optional(),
        user_subscription_status: z.boolean().optional(),
      }),
      responses: {
        201: z.object({
          status: z.literal('accepted'),
          addedComment: GetCommentsResponse,
        }),
        400: z.object({
          status: z.literal('rejected'),
          reason: z.string(),
        }),
      },
      // Use verifyAndPostComment instead.
      deprecated: true,
    },
    verifyAndPostComment: {
      summary:
        'Create comment for story episode, validating it for toxicity. Toxic comments are marked as is_blocked = true',
      method: 'POST',
      path: '/verify-and-post-comment',
      body: z.object({
        story: z.string(),
        body: z.string(),
        episode: z.number(),
        username: z.string(),
        user_id: z.string(),
        firebase_id: z.string(),
        body_length: z.number().optional(),
        ts: z.number(),
        path: z.string(),
        parent_comment_id: z.number().optional(),
        user_avatar: z.string().optional(),
        user_subscription_status: z.boolean().optional(),
        files: z.array(z.string()).optional(),
      }),
      responses: {
        201: z.object({
          status: z.literal('accepted'),
          addedComment: GetCommentsResponse,
        }),
        400: z.object({
          status: z.literal('rejected'),
          reason: z.string(),
        }),
      },
    },
    uploadCommentPicture: {
      summary: 'Upload comment picture',
      method: 'POST',
      path: '/comment-picture/:commentId',
      pathParams: z.object({
        commentId: z.coerce.number(),
      }),
      body: z.any(),
      responses: {
        201: z.object({
          message: z.string(),
          fileUrl: z.string(),
        }),
        400: z.object({
          error: z.string(),
        }),
        500: z.object({
          error: z.string(),
        }),
      },
    },
    reportComment: {
      summary: 'Report comment',
      method: 'POST',
      path: '/report-comment',
      body: z.object({
        comment_id: z.number(),
        reporter_user_id: z.string(),
        reason: z.enum(['spam', 'inappropriate', 'harmful']),
      }),
      responses: {
        201: z.object({
          status: z.literal('accepted'),
        }),
        400: z.object({
          status: z.literal('rejected'),
          reason: z.string(),
        }),
      },
    },
    bulkReportComments: {
      summary: 'Report multiple comments',
      method: 'POST',
      path: '/bulk-report-comments',
      body: z.array(
        z.object({
          comment_id: z.number(),
          reporter_user_id: z.string(),
          reason: z.enum(['spam', 'inappropriate', 'harmful']),
        }),
      ),
      responses: {
        200: z.array(
          z
            .object({
              status: z.literal('accepted'),
            })
            .or(
              z.object({
                status: z.literal('rejected'),
                reason: z.string(),
              }),
            ),
        ),
      },
    },
    getReactionsCount: {
      summary: 'Get likes count for specific comment id and user reaction',
      method: 'GET',
      path: '/reactions',
      query: z.object({
        commentId: z.number(),
      }),
      responses: {
        200: z.object({
          likesCount: z.number(),
          isUserLikedComment: z.boolean(),
        }),
      },
    },
    addUserReactionToComment: {
      summary: 'Add likes for specific comment id',
      method: 'POST',
      path: '/add-reaction',
      body: z.object({
        commentId: z.number(),
      }),
      responses: {
        201: z.literal('Success'),
      },
    },
    getIsUserCanReceiveSocialNotification: {
      summary: 'Check that reply to UserId can receive notificatio',
      method: 'GET',
      path: '/check-reply-to-user-can-receive-notification',
      query: z.object({
        recipientUserId: z.string(),
      }),
      responses: {
        200: z.object({
          replyToUserCanReceiveNotification: z.boolean(),
        }),
      },
    },
    getCommentById: {
      summary: 'Get comment by id',
      method: 'GET',
      path: '/get-comment',
      query: z.object({
        commentId: z.number(),
      }),
      responses: {
        200: z.object({
          comment: GetCommentsResponse.optional(),
          status: z.string(),
        }),
      },
    },
    getUserCommentCount: {
      summary: 'Get comments count for episode',
      method: 'GET',
      path: '/user-comment-count',
      responses: {
        200: z.object({
          count: z.number(),
        }),
      },
    },
  },
  {
    pathPrefix: '/comments',
  },
);
